import {useEffect, useState} from "react";
import {Route, Routes, useLocation} from "react-router-dom";
import {getCurrentUser, logout} from "../api/client";
import {clearStoredAuth, getStoredAuth, saveAuth} from "../auth/authStorage";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import Contacts from "../components/modals/Contacts";
import Home from "../features/home/Home";
import Admin from "../pages/admin/Admin";
import Account from "../pages/account/Account";
import Address from "../pages/address/Address";
import Login from "../pages/auth/Login";
import Register from "../pages/auth/Register";
import Prices from "../pages/prices/Prices";
import Request from "../pages/request/Request";
import Staff from "../pages/staff/Staff";
import StaffApply from "../pages/staff/StaffApply";
import Terms from "../pages/terms/Terms";

function App() {
    const location = useLocation();
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [auth, setAuth] = useState(() => getStoredAuth());

    useEffect(() => {
        const storedAuth = getStoredAuth();
        if (!storedAuth?.token) {
            return;
        }

        let isMounted = true;

        getCurrentUser(storedAuth.token)
            .then((user) => {
                if (isMounted) {
                    setAuth({
                        ...storedAuth,
                        user,
                    });
                }
            })
            .catch(() => {
                if (isMounted) {
                    clearStoredAuth();
                    setAuth(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, [location.pathname]);

    function handleAuthSuccess(authResponse, remember) {
        saveAuth(authResponse, remember);
        setAuth(authResponse);
    }

    async function handleLogout() {
        const token = auth?.token;

        if (isLoggingOut) {
            return;
        }

        setIsLoggingOut(true);
        await new Promise((resolve) => {
            setTimeout(resolve, 520);
        });

        clearStoredAuth();
        setAuth(null);
        setIsLoggingOut(false);

        if (!token) {
            return;
        }

        try {
            await logout(token);
        } catch {
            clearStoredAuth();
        }
    }

    const pageContentClassName = [
        "page-content",
        isContactOpen ? "page-content-blurred" : "",
        isLoggingOut ? "page-content-logging-out" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="App">
            <div className={pageContentClassName}>
                <Header
                    currentUser={auth?.user}
                    isLoggingOut={isLoggingOut}
                    onContactClick={() => setIsContactOpen(true)}
                    onLogout={handleLogout}
                />
                <Routes>
                    <Route path="/" element={<Home/>}/>
                    <Route path="/login" element={<Login onAuthSuccess={handleAuthSuccess}/>}/>
                    <Route path="/register" element={<Register onAuthSuccess={handleAuthSuccess}/>}/>
                    <Route
                        path="/account"
                        element={(
                            <Account
                                auth={auth}
                                section="overview"
                                onContactClick={() => setIsContactOpen(true)}
                                onLogout={handleLogout}
                            />
                        )}
                    />
                    <Route
                        path="/account/profile"
                        element={(
                            <Account
                                auth={auth}
                                section="profile"
                                onContactClick={() => setIsContactOpen(true)}
                                onLogout={handleLogout}
                            />
                        )}
                    />
                    <Route
                        path="/account/vehicle"
                        element={(
                            <Account
                                auth={auth}
                                section="vehicle"
                                onContactClick={() => setIsContactOpen(true)}
                                onLogout={handleLogout}
                            />
                        )}
                    />
                    <Route
                        path="/account/requests"
                        element={(
                            <Account
                                auth={auth}
                                section="requests"
                                onContactClick={() => setIsContactOpen(true)}
                                onLogout={handleLogout}
                            />
                        )}
                    />
                    <Route
                        path="/account/settings"
                        element={(
                            <Account
                                auth={auth}
                                section="settings"
                                onContactClick={() => setIsContactOpen(true)}
                                onLogout={handleLogout}
                            />
                        )}
                    />
                    <Route path="/admin" element={<Admin auth={auth}/>}/>
                    <Route path="/address" element={<Address/>}/>
                    <Route path="/prices" element={<Prices/>}/>
                    <Route path="/request" element={<Request auth={auth} onContactClick={() => setIsContactOpen(true)}/>}/>
                    <Route path="/staff" element={<Staff/>}/>
                    <Route path="/staff/apply" element={<StaffApply auth={auth}/>}/>
                    <Route path="/terms" element={<Terms/>}/>
                </Routes>
                <Footer/>
            </div>
            <Contacts
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />
        </div>
    );
}

export default App;
